<template>
  <main class="packages-page">
    <section class="packages-hero">
      <div>
        <p class="eyebrow">Bundles and packages</p>
        <h2>Support plans designed for growing teams and busy business owners.</h2>
        <p class="lead">
          Choose a managed IT package for ongoing support, then layer in project bundles
          for websites, applications, infrastructure, or security upgrades.
        </p>
      </div>

      <div class="hero-panel">
        <p>Best for businesses that want predictable support, fast response times, and a single trusted IT partner.</p>
        <router-link class="cta-dark" to="/#quote">Request a custom quote</router-link>
      </div>
    </section>

    <section class="tier-grid">
      <article v-for="tier in packages" :key="tier.name" class="tier-card" :class="{ featured: tier.featured }">
        <p class="tier-label">{{ tier.label }}</p>
        <h3>{{ tier.name }}</h3>
        <p class="tier-copy">{{ tier.copy }}</p>
        <ul>
          <li v-for="feature in tier.features" :key="feature">{{ feature }}</li>
        </ul>
      </article>
    </section>

    <section class="solution-bundles">
      <div class="section-heading">
        <p class="eyebrow">Solution bundles</p>
        <h3>Package complementary services into one delivery plan.</h3>
      </div>

      <div class="bundle-grid">
        <article v-for="bundle in bundles" :key="bundle.name" class="bundle-card">
          <h4>{{ bundle.name }}</h4>
          <p>{{ bundle.copy }}</p>
          <ul>
            <li v-for="item in bundle.items" :key="item">{{ item }}</li>
          </ul>
        </article>
      </div>
    </section>

    <section class="addons">
      <div class="section-heading">
        <p class="eyebrow">Add-ons</p>
        <h3>Optional services that can be attached to any package.</h3>
      </div>

      <div class="addon-list">
        <span v-for="addon in addons" :key="addon">{{ addon }}</span>
      </div>
    </section>
  </main>
</template>

<script>
export default {
  name: 'PackagesView',
  data() {
    return {
      packages: [
        {
          label: 'Entry support',
          name: 'Basic Package',
          copy: 'A lean support plan for small teams that need dependable maintenance and fast remote help.',
          features: ['Remote support', 'System monitoring', 'Monthly health checks']
        },
        {
          label: 'Most popular',
          name: 'Standard Package',
          copy: 'A balanced plan for businesses that want a mix of remote care, onsite help, and backup oversight.',
          featured: true,
          features: ['Remote support', 'Onsite support', 'Security updates', 'Backup monitoring']
        },
        {
          label: 'Full coverage',
          name: 'Premium Package',
          copy: 'A fully managed relationship with proactive security, strategic planning, and priority assistance.',
          features: [
            'Full IT management',
            'Server administration',
            'Cybersecurity monitoring',
            'Priority response',
            'Strategic IT consulting'
          ]
        }
      ],
      bundles: [
        {
          name: 'Digital Launch Bundle',
          copy: 'Ideal for new or expanding companies that need a modern web presence and business workflow support.',
          items: ['Corporate website', 'Business email setup', 'Cloud configuration', 'Ongoing maintenance plan']
        },
        {
          name: 'Secure Office Bundle',
          copy: 'Designed for offices that need secure connectivity, visibility, and better protection across the workplace.',
          items: ['Network design', 'Wireless setup', 'CCTV integration', 'Threat monitoring review']
        },
        {
          name: 'Custom App Bundle',
          copy: 'A focused delivery package for organizations that want to digitize a process or serve customers online.',
          items: ['Discovery workshop', 'Web or mobile application build', 'API integration', 'Deployment support']
        }
      ],
      addons: [
        'Backup and disaster recovery',
        'Cloud migration',
        'Cloud security hardening',
        'API development',
        'Android and iOS app support',
        'Website refresh projects',
        'Server installation and maintenance',
        'Quarterly IT strategy reviews'
      ]
    }
  }
}
</script>

<style scoped lang="scss">
.packages-page {
  padding: clamp(1.3rem, 3vw, 2.5rem);
  max-width: 1200px;
  margin: 0 auto;
}

.packages-hero,
.solution-bundles,
.addons {
  margin-bottom: 2rem;
}

.packages-hero {
  display: grid;
  grid-template-columns: 1.4fr 0.8fr;
  gap: 1.5rem;
  align-items: stretch;
}

.packages-hero h2,
.section-heading h3 {
  margin: 0.3rem 0 0.8rem;
  font-size: clamp(2rem, 4vw, 3.3rem);
  line-height: 0.98;
  font-family: Impact, Haettenschweiler, "Arial Narrow Bold", sans-serif;
}

.lead,
.hero-panel p,
.tier-copy,
.bundle-card p {
  font-size: 1.02rem;
  line-height: 1.7;
  color: #294332;
}

.eyebrow,
.tier-label {
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--brand-green-deep);
}

.hero-panel,
.tier-card,
.bundle-card,
.addons {
  border: 1px solid var(--brand-line);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0 22px 50px rgba(11, 30, 17, 0.08);
}

.hero-panel {
  padding: 1.5rem;
  background:
    linear-gradient(160deg, rgba(45, 200, 77, 0.18), rgba(9, 17, 12, 0.96)),
    #101712;
  color: #fff;
}

.hero-panel p {
  color: rgba(255, 255, 255, 0.9);
}

.cta-dark {
  display: inline-flex;
  margin-top: 1rem;
  padding: 0.9rem 1.2rem;
  border-radius: 999px;
  background: var(--brand-green);
  color: var(--brand-black);
  font-weight: 800;
}

.tier-grid,
.bundle-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.2rem;
}

.tier-card,
.bundle-card {
  padding: 1.5rem;
}

.tier-card.featured {
  background: linear-gradient(180deg, #ecfff0 0%, #ffffff 100%);
  border-color: rgba(45, 200, 77, 0.45);
  transform: translateY(-8px);
}

.tier-card h3,
.bundle-card h4 {
  margin: 0.35rem 0 0.7rem;
  font-size: 1.6rem;
}

.tier-card ul,
.bundle-card ul {
  margin: 0;
  padding-left: 1.1rem;
  line-height: 1.8;
}

.addon-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  margin-top: 1rem;
}

.addon-list span {
  padding: 0.85rem 1rem;
  border-radius: 999px;
  background: #eff9f0;
  border: 1px solid rgba(45, 200, 77, 0.22);
  font-weight: 700;
}

@media (max-width: 920px) {
  .packages-hero,
  .tier-grid,
  .bundle-grid {
    grid-template-columns: 1fr;
  }

  .tier-card.featured {
    transform: none;
  }
}
</style>
